package org.gymly.i18n

import org.gymly.model.AdminNavGroup
import org.gymly.model.AdminPersonSex
import org.gymly.model.AdminRoute
import org.gymly.model.AdminWeekday

object StringsEn : GymlyStrings {
    override val locale = GymlyLocale.EN
    override val common = object : CommonStrings {
        override val save = "Save"
        override val saveChanges = "Save changes"
        override val saving = "Saving…"
        override val cancel = "Cancel"
        override val delete = "Delete"
        override val remove = "Remove"
        override val edit = "Edit"
        override val add = "Add"
        override val create = "Create"
        override val close = "Close"
        override val search = "Search"
        override val clear = "Clear"
        override val retry = "Retry"
        override val loading = "Loading…"
        override val noResults = "No results"
        override val confirm = "Confirm"
        override val yes = "Yes"
        override val no = "No"
        override val all = "All"
        override val select = "Select"
        override val active = "Active"
        override val inactive = "Inactive"
        override val actions = "Actions"
        override val status = "Status"
        override val total = "Total"
        override val date = "Date"
        override val amount = "Amount"
        override val name = "First name"
        override val lastName = "Last name"
        override val email = "Email"
        override val phone = "Phone"
        override val address = "Address"
        override val document = "ID document"
        override val sex = "Sex"
        override val notes = "Notes"
        override val expand = "Expand"
        override val collapse = "Collapse"
        override val previous = "Previous"
        override val next = "Next"
        override val emptyValue = "—"
        override val genericError = "Something went wrong."
        override val noRecords = "No records."
        override val listLoadFailed = "Could not load the list."
        override fun sexLabel(value: AdminPersonSex) = when (value) {
            AdminPersonSex.MALE -> "Male"
            AdminPersonSex.FEMALE -> "Female"
            AdminPersonSex.OTHER -> "Other"
        }

        override fun weekdayShort(day: AdminWeekday) = when (day) {
            AdminWeekday.MONDAY -> "Mon"
            AdminWeekday.TUESDAY -> "Tue"
            AdminWeekday.WEDNESDAY -> "Wed"
            AdminWeekday.THURSDAY -> "Thu"
            AdminWeekday.FRIDAY -> "Fri"
            AdminWeekday.SATURDAY -> "Sat"
            AdminWeekday.SUNDAY -> "Sun"
        }

        override fun weekdayLong(day: AdminWeekday) = when (day) {
            AdminWeekday.MONDAY -> "Monday"
            AdminWeekday.TUESDAY -> "Tuesday"
            AdminWeekday.WEDNESDAY -> "Wednesday"
            AdminWeekday.THURSDAY -> "Thursday"
            AdminWeekday.FRIDAY -> "Friday"
            AdminWeekday.SATURDAY -> "Saturday"
            AdminWeekday.SUNDAY -> "Sunday"
        }

        override fun monthShort(month: Int) = MONTHS_EN.getOrElse(month) { "" }
    }

    override val nav = object : NavStrings {
        override fun route(route: AdminRoute) = when (route) {
            AdminRoute.Dashboard -> "Dashboard"
            AdminRoute.BulkSignup -> "Bulk entry"
            AdminRoute.Clients -> "Clients"
            AdminRoute.Workers -> "Staff"
            AdminRoute.Services -> "Services"
            AdminRoute.MembershipPlans -> "Membership plans"
            AdminRoute.MembershipOrders -> "Membership orders"
            AdminRoute.Memberships -> "Memberships"
            AdminRoute.ClientAccounts -> "Digital wallet"
            AdminRoute.Orders -> "Orders"
            AdminRoute.Payments -> "Payments"
            AdminRoute.Marketing -> "Marketing"
            AdminRoute.Products -> "Products"
            AdminRoute.StoreSales -> "Sales"
            AdminRoute.Schedules -> "Weekly schedules"
            AdminRoute.Bookings -> "Attendance & sessions"
            AdminRoute.Profile -> "Profile"
            AdminRoute.Reports -> "Reports"
            AdminRoute.Settings -> "Settings"
            AdminRoute.TermsMonitoring -> "Terms monitoring"
        }

        override fun group(group: AdminNavGroup) = when (group) {
            AdminNavGroup.People -> "Staff & clients"
            AdminNavGroup.Sales -> "Catalog & sales"
            AdminNavGroup.Store -> "Store"
            AdminNavGroup.Agenda -> "Schedule & bookings"
            AdminNavGroup.System -> "System"
        }
    }

    override val shell = object : ShellStrings {
        override val searchPlaceholder = "Search the whole app..."
        override val notifications = "Notifications"
        override val menu = "Menu"
        override val logout = "Log out"
        override fun searchType(type: String) = when (type) {
            "CLIENT" -> "Client"
            "STAFF" -> "Staff"
            "MEMBERSHIP" -> "Membership"
            "PLAN" -> "Plan"
            "PAYMENT" -> "Payment"
            "BOOKING" -> "Booking"
            "PRODUCT" -> "Product"
            else -> type
        }
    }

    override val profile = object : ProfileStrings {
        override val title = "Profile"
        override val subtitle = "Update your staff details, photo and password."
        override val personalData = "Personal details"
        override val workData = "Employment details"
        override val firstNameRequired = "First name *"
        override val lastNameRequired = "Last name *"
        override val firstNamePlaceholder = "First name"
        override val lastNamePlaceholder = "Last name"
        override val emailPlaceholder = "name@example.com"
        override val phonePlaceholder = "e.g. +58 412 0000000"
        override val documentPlaceholder = "ID or passport"
        override val addressRequired = "Address *"
        override val addressPlaceholder = "Street, number, city"
        override val sexRequired = "Sex *"
        override val language = "Language"
        override val languageSavedHint = "Saved as soon as you tap a language."
        override val spanish = "Español"
        override val english = "English"
        override val role = "Role"
        override val branch = "Branch"
        override val workSchedule = "Work schedule"
        override val workScheduleEmpty = "No schedule defined"
        override val workScheduleOffDay = "Off"
        override val joinedAt = "Joined"
        override val changePassword = "Change password"
        override val changePasswordHint = "Leave empty to keep your current password."
        override val currentPassword = "Current password"
        override val newPassword = "New password"
        override val confirmNewPassword = "Confirm new password"
        override val newPasswordPlaceholder = "At least 8 characters"
        override val confirmPasswordPlaceholder = "Repeat the new password"
        override val profilePhoto = "Profile photo"
        override val uploadPhoto = "Upload photo"
        override val changePhoto = "Change photo"
        override val removePhoto = "Remove photo"
        override val photoNotSaved = "The photo was not saved on the server. Try Save changes."
        override val photoNotSavedRetry = "The photo was not saved. Please try again."
        override val photoSaveHint = "Press Save changes if it did not upload on its own."
        override val noImageSelected = "No image was selected."
        override val photoUpdated = "Photo updated and synced with the access terminal."
        override val photoUploadFailed = "Could not upload the photo. Try Save changes."
        override val photoDeleted = "Photo removed."
        override val photoDeleteFailed = "Could not remove the photo."
        override val profileLoadFailed = "Could not load your profile."
        override val profileSaveFailed = "Could not save your profile."
        override val nameRequiredError = "First name and last name are required."
        override val addressRequiredError = "Address is required."
        override val sexRequiredError = "Sex is required."
        override val needCurrentPassword = "Enter your current password."
        override val passwordTooShort = "The new password must be at least 8 characters."
        override val passwordsDoNotMatch = "The passwords do not match."
        override val savedProfile = "Profile saved."
        override val savedProfileAndPassword = "Profile and password updated."
        override val savedPhotoAndPassword = "Photo and password updated."
        override val savedPhotoAndProfile = "Photo uploaded and profile saved."
        override fun selectedFile(name: String) = "Selected: $name"
    }

    override val dashboard = object : DashboardStrings {
        override val loadFailed = "Could not load the dashboard."
        override val quickActions = "Quick actions"
        override val quickActionsHint = "Front-desk shortcuts — the day-to-day."
        override val registerSale = "Register sale"
        override val registerSaleHint = "Store · merch and supplements"
        override val membershipOrder = "Membership order"
        override val membershipOrderHint = "Enroll and charge"
        override val bulkEntry = "Bulk entry"
        override val bulkEntryHint = "Enroll or charge in batch"
        override val assignMembership = "Assign membership"
        override val assignMembershipHint = "Member + plan + trainer"
        override val membershipPlans = "Membership plans"
        override val membershipPlansHint = "Plan catalog"
        override val attendanceSessions = "Attendance and sessions"
        override val attendanceSessionsHint = "Floor, bookings and staff"
        override val activity = "Activity"
        override val bookingsTodayWeek = "Bookings today / week"
        override val paymentsOnTime = "On-time payments"
        override val activeClients = "active clients"
        override val trainers = "trainers"
        override val scheduleSlots = "slots"
        override val closeDoor = "Close door"
        override val closeDoorHint = "If it did not close by itself after 10 s"
        override val doorClosed = "Door closed."
        override val doorCloseFailed = "Could not close the door."
        override val accessTitle = "Access · terminal"
        override val accessHint = "Hikvision check-ins and simulations."
        override val accessEmpty = "No events yet. Set up the terminal in Settings → Access."
        override val unknownPerson = "Unknown"
        override val trainerSchedule = "Trainer schedule"
        override val noSlotsToShow = "No slots to show."
        override val allTrainers = "All trainers"
        override val allTrainersHint = "Filter the dashboard grid by picking a trainer below."
        override val noTrainers = "No users with the TRAINER role."
        override val addTrainer = "+ ADD TRAINER"
        override val calendarTitle = "Calendar · bookings"
        override val noBookingsInPeriod = "No bookings in this period."
        override fun bookingsDetail(today: Long, week: Long) =
            "$today of $week confirmed this week"

        override fun paymentsDetail(percent: Int) = "$percent% (PAID vs PENDING)"
        override fun weekBookingsSummary(count: Int) =
            "Monday to Friday · $count booking(s) this week"

        override fun bookedOfCapacity(booked: Int, capacity: Int) = "$booked/$capacity bkd."
        override fun panelFilter(trainerCount: Int) = "Dashboard filter · $trainerCount trainers"
    }
}

private val MONTHS_EN = listOf(
    "", "Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
)
